import React, { useState, useEffect } from 'react';
import { useQuery, useReactiveVar } from '@apollo/client';
import { Page } from '../../../../layout';
import PageInstructions from '@shared/components/PageInstructions';
import { Button, FormGroup, FormControlLabel } from '@mui/material';
import MuiSwitch from '@shared/components/MuiSwitch';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { checkPointData } from '../../../checkPointsCache';
import ErrorMessage from '@shared/components/ErrorMessage';
import { isEqual } from 'lodash';
import PerformanceMetricPageEditViewControls from '../PerformanceMetricPageEditViewControls';
import ApolloError from '@shared/components/ApolloError';
import { Breadcrumb } from '@shared/components/breadcrumb/Breadcrumb';
import { getPageTitle } from '@shared/services/pageTitleService';
import GET_ORG_UNIT_INFO from '@shared/queries/getOrgUnitInfoQuery';
import {
  getPerformanceReportMetricFieldsDefaultErrors,
  metricProperties
} from '../pageSettings/pageSettingsByPageMode';
import LinkButton from '@shared/components/LinkButton';
import { userPrivileges, userTier } from '@apollo-internal/cache';
import { canUserAccessSeveralOrganization } from '@shared/services/userAccessibilityService';
import OrgUnitBreadcrumbAnnotation from '@shared/components/breadcrumb/annotations/OrgUnitBreadcrumbAnnotation';
import { PAGE_VIEW_MODES } from '../../../../../constants/page-view-modes';
import { resetTableFieldsMap } from '../../constants/resetTableFieldsMap';
import ConfirmDialogWithButton from '@shared/components/confirmDialog/ConfirmDialogWithButton';
import { getDataNotFoundPage } from '@shared/services/getDataNotFoundPageService';
import PrivilegeNames from '../../../../../constants/privilegeNames';
import { PerformanceMetricService } from '../services/performanceMetricService';
import { PerformanceMetricValidator } from '../../validators/PerformanceMetricValidator.js';

/**
 * @typedef {object} EditPerformanceMetricProps
 * @property {*} [pageMode]
 */

/**
 * @param {EditPerformanceMetricProps} props
 */
const EditPerformanceMetric = props => {
  const { pageMode = PAGE_VIEW_MODES.ADD } = props;

  const { orgUnit, appraisal, checkPointId } = useParams();
  const orgUnitId = parseInt(orgUnit);
  const appraisalId = parseInt(appraisal);
  const selectedCheckPointId = parseInt(checkPointId);

  const checkPointCache = useReactiveVar(checkPointData);
  const privileges = useReactiveVar(userPrivileges);
  const canEditNonEditableFields = privileges.includes(
    PrivilegeNames.CheckPoints_Edit_NonEditableFields
  );

  const tier = useReactiveVar(userTier);

  const pageSettings = getPageSettings();
  const previousLink = `/check-points/${orgUnitId}/${pageMode}-check-point/${appraisalId}/${selectedCheckPointId}`;

  const performanceMetric = checkPointCache.selectedPerformanceMetric;

  const [showValidationMessage, setShowValidationMessage] = useState(false);
  const [showDescriptions, setShowDescriptions] = useState(false);
  const [validationErrors, setValidationErrors] = useState({
    ...pageSettings.performanceReportMetricFieldsErrorObject()
  });

  const [formData, setFormData] = useState({ ...performanceMetric });
  const [isSubmitBtnDisabled, setIsSubmitBtnDisabled] = useState(true);
  const [selectedMetricProperty, setSelectedMetricProperty] = useState(null);

  const navigate = useNavigate();

  const {
    data: orgUnitData,
    error: orgUnitError,
    loading: orgUnitLoading
  } = useQuery(GET_ORG_UNIT_INFO, {
    variables: { id: orgUnitId }
  });

  const orgUnitName = orgUnitData && orgUnitData.mddapOrgUnit && orgUnitData.mddapOrgUnit.Name;
  const orgName =
    orgUnitData && orgUnitData.mddapOrgUnit && orgUnitData.mddapOrgUnit.MddapOrganization.Name;
  const renderedOrgUnit = orgUnitData && (
    <OrgUnitBreadcrumbAnnotation orgUnit={orgUnitData.mddapOrgUnit} key={orgUnitName} />
  );

  const pageTitle = getPageTitle([orgUnitName, 'Edit Performance Report Metric']);
  const commonItems = [renderedOrgUnit, `Appraisal ${appraisalId}`];
  const annotationItems = canUserAccessSeveralOrganization(tier)
    ? [orgName, ...commonItems]
    : commonItems;

  const breadcrumbPathItems = [
    { name: 'Check Points', url: `/check-points/${orgUnitId}` },
    {
      name: pageMode === PAGE_VIEW_MODES.EDIT ? 'Edit Check Point' : 'Add Check Point',
      url: previousLink
    },
    { name: 'Edit Performance Report Metric', url: '' }
  ];

  useEffect(() => {
    if (isSubmitBtnDisabled && !isEqual(formData, performanceMetric)) {
      setIsSubmitBtnDisabled(false);
    }
  }, [formData, isSubmitBtnDisabled, pageSettings, performanceMetric]);

  const handleClickOutside = () => {
    setSelectedMetricProperty(null);
  };

  const handleOnClickMetricField = clickedField => {
    const metricProperty = pageSettings.metricProperties.find(x => x.field === clickedField);
    setSelectedMetricProperty(metricProperty);
  };

  const handleFieldChange = (serviceMethod, param) => {
    const { newFormData, newValidationErrors } = serviceMethod(
      param,
      formData,
      validationErrors,
      getDefaultErrorObject
    );
    setFormData(newFormData);
    setValidationErrors(newValidationErrors);
  };

  const handleOnChange = param => {
    updateValidationErrors(param);
    setFormData({ ...formData, ...param });
  };

  const handleOnChangeStatus = param => {
    let updatedFormData = { ...formData, ...param };

    const fields = resetTableFieldsMap(performanceMetric.referenceCheckpointDatapoint).get(
      param.status
    );

    fields.forEach(({ field, value }) => {
      updatedFormData[field] = value;
    });

    setFormData(updatedFormData);
    setValidationErrors(pageSettings.performanceReportMetricFieldsErrorObject());
  };

  const handleOnChangeLowerUpperLimit = param => {
    const { newFormData, newValidationErrors } =
      PerformanceMetricService.handleOnChangeLowerUpperLimit(
        param,
        formData,
        validationErrors,
        getNewErrorObject
      );
    setFormData(newFormData);
    setValidationErrors(newValidationErrors);
  };

  const handleOnChangeActual = param => {
    handleFieldChange(PerformanceMetricService.handleOnChangeActual, param);
  };

  const handleOnChangeQualityDomain = param => {
    handleFieldChange(PerformanceMetricService.handleOnChangeQualityDomain, param);
  };

  const handleOnChangeMeasurementCalculation = param => {
    handleFieldChange(PerformanceMetricService.handleOnChangeMeasurementCalculation, param);
  };

  const handleOnChangeTimeBounds = param => {
    handleFieldChange(PerformanceMetricService.handleOnChangeTimeBounds, param);
  };

  const isFormValid = () => {
    const fields = pageSettings.metricProperties
      .filter(x => x.field !== 'qualityDomainDefinition')
      .map(x => x.field);
    const errors = PerformanceMetricValidator.validatePerformanceMetric(
      formData,
      validationErrors,
      fields,
      pageSettings,
      getNewErrorObject,
      getDefaultErrorObject
    );
    setValidationErrors(errors);
    setShowValidationMessage(true);
    return !hasErrors(errors);
  };

  const updateValidationErrors = param => {
    const value = Object.values(param)[0];
    const key = Object.keys(param)[0];

    setValidationErrors({
      ...validationErrors,
      [`${key}`]: getNewErrorObject(validationErrors[`${key}`], { hasError: !value })
    });
  };

  const hasErrors = errors => Object.values(errors).filter(x => x.hasError).length > 0;

  const getDefaultErrorObject = prevError => {
    return { ...prevError, ...{ hasError: false, errorMessage: '' } };
  };

  const getNewErrorObject = (prevError, error) => {
    return { ...prevError, ...error };
  };

  const redirectToCheckPointsPage = state => {
    navigate(previousLink, { state });
  };

  const handleCancel = () => {
    setFormData({ ...performanceMetric });
  };

  const handleSubmit = () => {
    if (isFormValid()) {
      updatePerformanceReportMetric();
      redirectToCheckPointsPage();
    }
  };

  const handleDeleteMetric = () => {
    removePerformanceReportMetric();
    redirectToCheckPointsPage({ successMessage: 'Metric deleted successfully.' });
  };

  const removePerformanceReportMetric = () => {
    const cachedPerformanceMetrics = checkPointCache.performanceMetrics.filter(
      x => x.tempId !== formData.tempId
    );
    checkPointData({ ...checkPointCache, performanceMetrics: cachedPerformanceMetrics });
  };

  const updatePerformanceReportMetric = () => {
    const cachedPerformanceMetrics = checkPointCache.performanceMetrics;
    const metric = {
      ...formData,
      lowerLimit: formData.lowerLimit ? parseFloat(formData.lowerLimit) : null,
      upperLimit: formData.upperLimit ? parseFloat(formData.upperLimit) : null,
      actual: formData.actual ? parseFloat(formData.actual) : null
    };

    const metricPreviousData = checkPointCache.performanceMetrics.find(
      x => x.tempId === metric.tempId
    );
    const editedMetricData = { ...metricPreviousData, ...metric };

    const updatedCachedPerformanceMetrics = cachedPerformanceMetrics.filter(
      x => x.tempId !== metric.tempId
    );
    updatedCachedPerformanceMetrics.push(editedMetricData);

    checkPointData({ ...checkPointCache, performanceMetrics: updatedCachedPerformanceMetrics });
  };

  if (orgUnitData && orgUnitData.mddapOrgUnit === null) {
    return <Navigate to={getDataNotFoundPage(tier)} />;
  }

  if (orgUnitError) {
    return <ApolloError />;
  }

  return (
    <Page title={pageTitle} className='performance-report-metric-page'>
      <Breadcrumb
        pathItems={breadcrumbPathItems}
        annotationItems={annotationItems}
        loading={orgUnitLoading}
      />

      <div className='performance-report-metric-page__container' onClick={handleClickOutside}>
        <PerformanceMetricPageEditViewControls
          performanceMetric={formData}
          handleOnChangeStatus={handleOnChangeStatus}
          handleOnChangeLowerUpperLimit={handleOnChangeLowerUpperLimit}
          handleOnChangeActual={handleOnChangeActual}
          handleOnChange={handleOnChange}
          handleOnClickMetricField={handleOnClickMetricField}
          handleOnChangeTimeBounds={handleOnChangeTimeBounds}
          handleOnChangeMeasurementCalculation={handleOnChangeMeasurementCalculation}
          handleOnChangeQualityDomain={handleOnChangeQualityDomain}
          selectedMetricProperty={selectedMetricProperty}
          showDescriptions={showDescriptions}
          validationErrors={validationErrors}
          pageSettings={pageSettings}
          canEditNonEditableFields={canEditNonEditableFields}
        />

        <div className='sticky-section'>
          <div className='instructions'>
            <div className='action-button-group'>
              <div className='action-buttons'>
                {performanceMetric.isDeletable && (
                  <ConfirmDialogWithButton
                    handleOnConfirm={handleDeleteMetric}
                    openModalBtnText='Delete'
                    openModalBtnClassName='delete-button'
                    message='Are you sure that you want to delete this metric?'
                    confirmBtnText='Delete'
                    cancelBtnText='Cancel'
                  />
                )}
                <Button
                  type='submit'
                  data-testid='submit-button'
                  className='primary-button'
                  onClick={handleSubmit}
                  disabled={isSubmitBtnDisabled}>
                  Submit
                </Button>

                <LinkButton
                  data-testid='cancel-button'
                  className='secondary-button'
                  type='button'
                  to={previousLink}
                  onClick={handleCancel}>
                  Cancel
                </LinkButton>
              </div>
              {showValidationMessage && hasErrors(validationErrors) && (
                <ErrorMessage
                  message='Metric errors must be resolved before submission.'
                  className='button-group-error-message'
                />
              )}
            </div>
            <PageInstructions
              title='Edit Performance Report Metric'
              description={pageSettings.description}
            />
          </div>
          <FormGroup className='switch-container'>
            <FormControlLabel
              key='FieldDescriptions'
              labelPlacement='start'
              label='Field Descriptions'
              className='switch-label'
              control={
                <MuiSwitch
                  id='FieldDescriptions'
                  checked={showDescriptions}
                  onChange={(_, checked) => setShowDescriptions(checked)}
                />
              }
            />
          </FormGroup>
        </div>
      </div>
    </Page>
  );
};

const getPageSettings = () => {
  return {
    firstSectionTitle: 'Editable Metric Information',
    secondSectionTitle: 'Non-Editable Metric Information',
    description: (
      <>
        Editable metric fields are displayed first and can be modified as needed but not left blank.
        Fields are dropdown selections, numerical entry, or open text. Click “Submit” to finalize
        metric edits, or “Cancel” to go back to the Add Check Point page. Field descriptions hidden
        by default and can be shown for reference. Uneditable metric fields are displayed at the
        bottom of the page and can be viewed by clicking the “+” button. For additional information,
        review the{' '}
        <a href='https://cmmiinstitute.com/resource-files/public/pcs/mddap/participants-only/mddap-check-point-guidelines'>
          MDDAP Check Point Guidelines
        </a>
        .
      </>
    ),
    metricProperties: metricProperties,
    performanceReportMetricFieldsErrorObject: getPerformanceReportMetricFieldsDefaultErrors
  };
};

export default EditPerformanceMetric;
